import { useEffect } from 'react';
import type { FetchError } from '@repo/api/client';
import {
  loanSignVerifyBadRequestResponseBodySchema,
  usePostLoanSignVerify,
  usePostLoanSignLiberis,
  usePostLoanSignPragma,
  loanSignTooManyAttemptsResponseBodySchema,
  type LoanSignRequestBodySchema,
  type LoanSignVerifyRequestBody,
} from '@repo/api/endpoints/loan';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import type { ResponseError } from '@repo/api/types';
import { useProviderStore } from '@repo/store/provider';
import { useEventClick } from '@repo/hooks';
import type { SmsId } from '@repo/shared/types';

import { useLoanProcessModal } from '@/components/loan-process/hooks/use-loan-process-modal';
import { ApplicationSuspended } from '@/components/loan-process/loan-stages/shared/application-suspended/application-suspended';
import {
  useLoanProcessStore,
  type LoanStoreData,
} from '@/components/loan-process/store/loan-process.store';
import { useMainHubStoreActions } from '@/store/main-hub.store';
import { VIEW_TYPE } from '@/store/view.type';
import { useRefetchMerchantOnStageChange } from '@/components/loan-process/hooks/use-refetch-merchant-on-stage-change';

import { LiberisIframe } from './liberis-iframe/liberis-iframe';
import { AgreementSmsConfirmation } from './agreement-sms-confirmation/agreement-sms-confirmation';

/**
 * It only catches wrong sms code error. If there's no error or there's error that
 * doesn't refer to wrong code it doesn't be caught
 */
const isWrongVerifySMSCodeError = (verifyError: ResponseError | null) => {
  if (!verifyError) return false;
  const error = verifyError as FetchError;
  const response = loanSignVerifyBadRequestResponseBodySchema.safeParse(error.data);

  return response.success && response.data.type === 'SMS_CODE_VERIFY' && error.status === 400;
};

/**
 * It catches particular error related to too many attempts of sending request on POST api/loan/sign
 */
const isTooManyAttemptsError = (signError: ResponseError | null) => {
  if (!signError) return false;
  const error = signError as FetchError;
  const response = loanSignTooManyAttemptsResponseBodySchema.safeParse(error.data);

  //status and code error which indicates on too many attempts of sending  requests for sms code
  return response.success && response.data.type === 'SMS_CODE' && error.status === 429;
};

interface StageThree {
  merchantResponseData: MerchantResponse | undefined;
  loanData: LoanStoreData | undefined;
  loanSMSIds: SmsId[];
}
export function StageThree({ merchantResponseData, loanData, loanSMSIds }: StageThree) {
  const { setLoanStage } = useLoanProcessModal();
  const {
    actions: { clearLoanProcessStore, pushLoanSMSId },
  } = useLoanProcessStore();

  const {
    state: { currentProvider },
  } = useProviderStore();

  const { setCurrentView } = useMainHubStoreActions();
  const trackClick = useEventClick(merchantResponseData);

  const closeModalAndGoToDashboard = () => {
    clearLoanProcessStore();
    setCurrentView(VIEW_TYPE.DASHBOARD);
  };

  useRefetchMerchantOnStageChange();

  const {
    mutate: mutatePragmaSign,
    status: signPragmaStatus,
    data: signPragmaData,
    error: signPragmaError,
  } = usePostLoanSignPragma();

  const {
    mutate: mutateLiberisSign,
    status: signLiberisStatus,
    data: signLiberisData,
    error: signLiberisError,
    isPending: signLiberisIsPending,
  } = usePostLoanSignLiberis();

  const {
    mutate: mutateVerify,
    status: verifyStatus,
    error: verifyError,
    isPending: verifyIsPending,
  } = usePostLoanSignVerify();

  const handleSignPragmaMutation = (data: LoanSignRequestBodySchema) => {
    mutatePragmaSign(data, {
      onSuccess: (mutationData) => {
        pushLoanSMSId(mutationData.data.smsId);
      },
    });
  };

  const handleSignLiberisMutation = (data: LoanSignRequestBodySchema) => {
    mutateLiberisSign(data);
  };

  const handleSubmitForm = (data: LoanSignVerifyRequestBody) => {
    mutateVerify(data);
  };

  const isApplicationSuspend =
    !loanData ||
    !merchantResponseData ||
    !currentProvider ||
    signPragmaError ||
    signLiberisError ||
    (verifyError && !isWrongVerifySMSCodeError(verifyError));

  useEffect(() => {
    if (verifyStatus === 'success') {
      if (loanData?.provider !== 'liberis') {
        trackClick({
          activity: 'PRAGMA_AGREEMENT_SIGN',
          previousActivity: 'PRAGMA_SUMMARY_ACCEPT',
        });
      }
      setLoanStage(5);
    }
  }, [loanData, setLoanStage, verifyStatus]);

  useEffect(() => {
    if (isTooManyAttemptsError(signPragmaError)) {
      setLoanStage(8);
    }
  }, [setLoanStage, signPragmaError]);

  if (isApplicationSuspend) {
    return <ApplicationSuspended />;
  }

  if (loanData.provider === 'liberis') {
    return (
      <LiberisIframe
        handleCloseButton={closeModalAndGoToDashboard}
        merchantResponseData={merchantResponseData}
        loanData={loanData}
        loanSMSIds={loanSMSIds}
        loanSignMutation={{
          data: signLiberisData,
          status: signLiberisStatus,
          handleSignMutation: handleSignLiberisMutation,
          isPending: signLiberisIsPending,
        }}
        loanVerifyMutation={{
          handleSubmitForm,
          status: verifyStatus,
          hasWrongSMSCode: isWrongVerifySMSCodeError(verifyError),
          isPending: verifyIsPending,
        }}
      />
    );
  }

  return (
    <AgreementSmsConfirmation
      selectedProvider={loanData.provider}
      handleCloseButton={closeModalAndGoToDashboard}
      merchantResponseData={merchantResponseData}
      loanData={loanData}
      loanSMSIds={loanSMSIds}
      loanSignMutation={{
        data: signPragmaData,
        status: signPragmaStatus,
        handleSignMutation: handleSignPragmaMutation,
      }}
      loanVerifyMutation={{
        handleSubmitForm,
        status: verifyStatus,
        hasWrongSMSCode: isWrongVerifySMSCodeError(verifyError),
      }}
    />
  );
}
